import { useEffect, useMemo, useRef, useState } from "react";
import { Compass, LocateFixed, Minus, Plus, MapPin } from "lucide-react";
import {
  clusterPhotos, fitPhotoBounds, formatGPS, gpsSourceLabel, hasPhotoGPS,
  openMapUrl, projectPoint, unprojectPoint,
} from "@/utils/photoGps";
import "./ProjectPhotoLocationMap.css";

const MIN_ZOOM = 3;
const MAX_ZOOM = 19;
const TILE_SIZE = 256;
const TILE_URL = "https://tile.openstreetmap.org";
const COPYRIGHT_URL = "https://www.openstreetmap.org/copyright";

function mapTiles(center, zoom, width, height) {
  const origin = projectPoint(center.lat, center.lng, zoom);
  const worldSize = 2 ** zoom;
  const startX = Math.floor((origin.x - width / 2) / TILE_SIZE);
  const endX = Math.floor((origin.x + width / 2) / TILE_SIZE);
  const startY = Math.max(0, Math.floor((origin.y - height / 2) / TILE_SIZE));
  const endY = Math.min(worldSize - 1, Math.floor((origin.y + height / 2) / TILE_SIZE));
  const tiles = [];
  for (let x = startX; x <= endX; x++) {
    for (let y = startY; y <= endY; y++) {
      const wrappedX = ((x % worldSize) + worldSize) % worldSize;
      tiles.push({
        key: `${zoom}-${x}-${y}`,
        src: `${TILE_URL}/${zoom}/${wrappedX}/${y}.png`,
        left: x * TILE_SIZE - origin.x + width / 2,
        top: y * TILE_SIZE - origin.y + height / 2,
      });
    }
  }
  return tiles;
}

/** Photo-location map only. External map tiles are fetched only after the user
 * clicks "Load map" in the parent. No project name, photo URL or EXIF payload
 * is forwarded to the tile provider.
 */
export default function ProjectPhotoLocationMap({ photos, activeId, onSelectPhoto, onViewPhoto, focusRequestId = 0 }) {
  const rootRef = useRef(null);
  const pointer = useRef(null);
  const [size, setSize] = useState({ width: 420, height: 315 });
  const [camera, setCamera] = useState(() => fitPhotoBounds(photos, 420, 315));
  const [group, setGroup] = useState([]);
  const [tileError, setTileError] = useState(false);
  const located = useMemo(() => photos.filter(hasPhotoGPS), [photos]);
  const active = located.find(p => p.id === activeId) || null;

  useEffect(() => {
    const element = rootRef.current;
    if (!element) return undefined;
    const measure = () => {
      setSize({ width: Math.max(200, element.clientWidth), height: Math.max(220, element.clientHeight) });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setCamera(fitPhotoBounds(located, size.width, size.height));
    setGroup([]);
  }, [located, size.width, size.height]);

  // A gallery GPS click (including a repeated click on the same photo) focuses
  // the corresponding pin; map pin grouping and Fit all remain unchanged.
  useEffect(() => {
    if (!active) return;
    setCamera(previous => ({ ...previous, lat: active.gps_latitude,
      lng: active.gps_longitude, zoom: Math.max(previous.zoom, 17) }));
  }, [active, activeId, focusRequestId, size.width, size.height]);

  const tiles = useMemo(
    () => mapTiles(camera, camera.zoom, size.width, size.height),
    [camera, size],
  );
  const clusters = useMemo(
    () => clusterPhotos(located, camera, camera.zoom, size.width, size.height),
    [located, camera, size],
  );

  const setZoom = (change) => {
    setCamera(prev => ({ ...prev, zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom + change)) }));
    setGroup([]);
  };

  const pointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("button, a")) return;
    pointer.current = {
      id: event.pointerId, x: event.clientX, y: event.clientY,
      origin: projectPoint(camera.lat, camera.lng, camera.zoom),
      zoom: camera.zoom,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const pointerMove = (event) => {
    const start = pointer.current;
    if (!start || start.id !== event.pointerId) return;
    const next = unprojectPoint(
      start.origin.x - (event.clientX - start.x),
      start.origin.y - (event.clientY - start.y), start.zoom,
    );
    setCamera(prev => ({
      ...prev,
      lat: Math.max(-85.05, Math.min(85.05, next.lat)),
      lng: Math.max(-180, Math.min(180, next.lng)),
    }));
  };
  const pointerEnd = () => { pointer.current = null; };

  const selectCluster = (cluster) => {
    setGroup(cluster.photos);
    if (cluster.photos.length === 1) {
      onSelectPhoto(cluster.photos[0].id);
      return;
    }
    // For a cluster, keep each photo accessible even when all coordinates match.
    onSelectPhoto(cluster.photos[0].id);
  };

  return (
    <section className="photo-gps-map" aria-label="GPS locations for photos in this project" data-testid="photo-gps-map">
      <div className="photo-gps-map__heading">
        <div><strong><MapPin size={15} /> Photo GPS Map</strong>
          <small>{located.length} of {photos.length} photos have stored coordinates</small>
        </div>
        <div className="photo-gps-map__buttons">
          <button aria-label="Fit all photo locations" title="Fit all photo locations"
            onClick={() => { setCamera(fitPhotoBounds(located, size.width, size.height)); setGroup([]); }}
            type="button"><LocateFixed size={16}/></button>
          <button aria-label="Zoom in" onClick={() => setZoom(1)} type="button"><Plus size={16}/></button>
          <button aria-label="Zoom out" onClick={() => setZoom(-1)} type="button"><Minus size={16}/></button>
        </div>
      </div>
      {located.length === 0 ? (
        <div className="photo-gps-map__empty">
          <Compass size={22}/>
          <strong>No GPS coordinates available</strong>
          <span>Photos without original or stored GPS remain in your gallery. Coordinates printed on an image need separate verification.</span>
        </div>
      ) : (
        <>
          <div className="photo-gps-map__viewport" ref={rootRef} data-testid="photo-gps-map-viewport"
            onPointerDown={pointerDown} onPointerMove={pointerMove}
            onPointerUp={pointerEnd} onPointerCancel={pointerEnd}
            role="group" aria-label="Drag to pan. Use plus and minus buttons to zoom.">
            {tiles.map(t => <img key={t.key} className="photo-gps-map__tile" alt="" draggable="false"
              src={t.src} onError={() => setTileError(true)}
              style={{ left: t.left, top: t.top }} />)}
            {clusters.map((c, i) => {
              const focused = c.photos.some(photo => photo.id === activeId);
              return (
                <button key={c.photos.map(p => p.id).join(":")}
                  className={`photo-gps-map__pin${focused ? " is-active" : ""}`}
                  style={{ left: c.x, top: c.y }}
                  data-testid={`photo-gps-map-pin-${i}`}
                  title={`${c.photos.length} photo(s) at this location`}
                  aria-label={`${c.photos.length} photo(s) at this location`}
                  onClick={() => selectCluster(c)} type="button">
                  {c.photos.length > 1 ? c.photos.length : <MapPin size={17}/>}
                </button>
              );
            })}
            {tileError && <span className="photo-gps-map__tile-error">Map tiles unavailable. Photo coordinates remain available below.</span>}
            <a className="photo-gps-map__attribution" href={COPYRIGHT_URL}
              target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>
          </div>
          {group.length > 1 && (
            <div className="photo-gps-map__group" data-testid="photo-gps-cluster-photos">
              <strong>{group.length} photos near this pin</strong>
              <div>
                {group.map(photo =>
                  <button key={photo.id} onClick={() => onSelectPhoto(photo.id)}
                    className={photo.id === activeId ? "is-active" : ""} type="button">
                    <img src={photo.thumbnail_url} alt={photo.original_filename} />
                  </button>
                )}
              </div>
            </div>
          )}
          {active && (
            <div className="photo-gps-map__selection" data-testid="photo-gps-selected-details">
              <img alt={active.original_filename} src={active.thumbnail_url} />
              <div>
                <strong>{active.original_filename}</strong>
                <span>{formatGPS(active)}</span>
                <small>{gpsSourceLabel(active)}</small>
                <div>
                  <button type="button" onClick={() => onViewPhoto(active)}>View photo</button>
                  <a href={openMapUrl(active)} target="_blank" rel="noopener noreferrer">Open map ↗</a>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
